import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, useTheme, Divider } from '@mui/material';
import { useMermaidDiagram } from '../../hooks/useMermaidDiagram';
import { nodeToStageMapping, stageDetails } from '../../data/processFlowData';
import { ProcessStep } from '@/types/processFlowTypes';

interface MermaidDiagramProps {
  isVisible: boolean;
  currentStage: string;
  selectedStep: ProcessStep | null;
  completedSteps: string[];
  onNodeClick: (node: ProcessStep) => void;
}

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({
  isVisible,
  currentStage,
  selectedStep,
  completedSteps,
  onNodeClick
}) => {
  const theme = useTheme();
  const { diagramHtml } = useMermaidDiagram(isVisible);

  // Enhanced effect to highlight both nodes and subgraphs
  useEffect(() => {
    if (!diagramHtml) return;

    const diagramContainer = document.getElementById('mermaid-diagram-container');
    if (!diagramContainer) return;

    // Clear previous highlighting
    const allNodes = diagramContainer.querySelectorAll('.node');
    allNodes.forEach(node => {
      node.classList.remove('current-stage', 'completed', 'selected-node');
    });

    // Clear subgraph highlights
    const allSubgraphs = diagramContainer.querySelectorAll('.cluster');
    allSubgraphs.forEach(subgraph => {
      subgraph.classList.remove('highlighted-stage');
    });

    // Highlight current stage's subgraph
    allSubgraphs.forEach(subgraph => {
      const subgraphId = subgraph.id.toLowerCase();
      if (
        (currentStage === 'input' && subgraphId.includes('input')) ||
        (currentStage === 'analysis' && subgraphId.includes('analysis')) ||
        (currentStage === 'review' && subgraphId.includes('review')) ||
        (currentStage === 'output' && subgraphId.includes('output'))
      ) {
        subgraph.classList.add('highlighted-stage');
      }
    });

    // Highlight nodes in current stage, completed nodes, and selected node
    allNodes.forEach(node => {
      const nodeId = node.id.split('-')[1];
      if (nodeId && nodeToStageMapping[nodeId] === currentStage) {
        node.classList.add('current-stage');
      }
      if (completedSteps.includes(nodeId)) {
        node.classList.add('completed');
      }
      if (nodeId === selectedStep?.id) {
        node.classList.add('selected-node');
      }
    });
  }, [currentStage, diagramHtml, completedSteps, selectedStep]);

  // Helper function to get node Information from node ID
  const getNodeDetails = (nodeId: string): ProcessStep | undefined => {
    const stageName = nodeToStageMapping[nodeId];
    if (!stageName) return undefined;
    
    const stage = stageDetails[stageName];
    if (!stage) return undefined;
    
    const step = stage.steps.find(step => step.id === nodeId);
    return {
      id: step?.id || nodeId,
      label: step?.label || 'Unknown',
      description: step?.description || 'No description available',
      instructions: step?.instructions || 'No instructions available'
    };
  };

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        p: 2, 
        height: '100%',
        minHeight: 400,
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column'
      }}
    >            
      <Box sx={{ 
        position: 'relative',
        flexGrow: 1,
        overflow: 'hidden',
        '& svg': { 
          maxWidth: '100%',
          height: 'auto',
          cursor: 'pointer',
          '& .node:hover': {
            opacity: 0.8,
            transition: 'opacity 0.2s'
          },
          '& .node.current-stage': {
            filter: 'drop-shadow(0 0 3px rgba(33, 150, 243, 0.7))',
            stroke: theme.palette.primary.main,
            strokeWidth: '3px'
          },
          '& .node.completed': {
            filter: 'drop-shadow(0 0 3px rgba(76, 175, 80, 0.7))',
            stroke: theme.palette.success.main,
            strokeWidth: '2px'
          },
          '& .node.selected-node': {
            filter: 'drop-shadow(0 0 10px rgba(33, 150, 243, 1))',
            stroke: theme.palette.primary.dark,
            strokeWidth: '5px',
            '& rect, & circle, & polygon, & ellipse': {
              fill: theme.palette.primary.main,
              stroke: theme.palette.primary.dark,
            },
            '& .label': {
              fontWeight: 'bold',
              fill: '#ffffff',
            },
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { filter: 'drop-shadow(0 0 8px rgba(33, 150, 243, 0.8))' },
              '50%': { filter: 'drop-shadow(0 0 12px rgba(33, 150, 243, 1))' },
              '100%': { filter: 'drop-shadow(0 0 8px rgba(33, 150, 243, 0.8))' }
            },
            transition: 'all 0.3s ease'
          },
          '& .cluster.highlighted-stage': {
            filter: 'drop-shadow(0 0 5px rgba(33, 150, 243, 0.4))',
            '& rect, & polygon': {
              stroke: theme.palette.primary.main,
              strokeWidth: '2px',
              strokeDasharray: 'none',
              fill: 'rgba(33, 150, 243, 0.2)'
            },
            '& .cluster-label': {
              fontWeight: 'bold',
              fill: theme.palette.primary.main,
              fontSize: '1.05em',
              letterSpacing: '0.01em',
              dominantBaseline: 'middle',
              dy: '0.3em',
              paintOrder: 'stroke',
              overflow: 'visible',
              textLength: 'auto',
            },
            '& foreignObject': {
              overflow: 'visible',
              width: '100%',
              height: 'auto !important',
              transform: 'translateY(-3px)'
            }
          }
        }
      }}>
        <div 
          id="mermaid-diagram-container"
          dangerouslySetInnerHTML={{ __html: diagramHtml }} 
          onClick={(e) => {
            const target = e.target as Element;
            const node = target.closest('.node');
            console.log("mermaid-diagram-container clicked", node);
            if (node) {
              const nodeId = node.id.split('-')[1];
              const nodeDetails = getNodeDetails(nodeId);
              onNodeClick({
                id: nodeDetails?.id || nodeId,
                label: nodeDetails?.label || 'Unknown',
                description: nodeDetails?.description || 'No description available',
                instructions: nodeDetails?.instructions || 'No instructions available'
              });
            }
          }}
        />
      </Box>
    </Paper>
  );
};

export default MermaidDiagram;
